"use client"

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContextBackend';
import { useEmployeeProfileStore } from '../stores/employeeProfileStore';
import type { EmployeeDetails } from '../stores/employeeProfileStore';
import {
  UserCircle,
  FileText,
  GearSix,
  ChatCircle,
  Buildings,
  CaretDown,
  Target,
  ListChecks,
  ClockCounterClockwise,
  Users,
  Star,
  TrendUp,
  UsersFour,
  CurrencyCircleDollar,
} from '@phosphor-icons/react';
import { Edit2, Save } from 'lucide-react';
import { Spinner } from '@t-one-internal/design-system';
import { useSubscriptionStore } from '../stores/subscriptionStore';

import { AboutSection } from './my-profile/sections/AboutSection';
import { JobDetailsSection } from './my-profile/sections/JobDetailsSection';
import EducationProfileSection from './my-profile/sections/EducationProfileSection';
import ExperienceProfileSection from './my-profile/sections/ExperienceProfileSection';
import { AnalyticsSection } from './my-profile/sections/AnalyticsSection';
import DocumentsSection from './my-profile/sections/DocumentsSection';
import { AdminSection } from './my-profile/sections/AdminSection';
import UpdatesSection from './my-profile/sections/UpdatesSection';
import { BenefitsSection } from './my-profile/sections/BenefitsSection';
import { ProfilePhotoEditor } from '../components/profile/ProfilePhotoEditor';
import { OrgHierarchyPathDisplay } from '../components/organization/OrgHierarchyPathDisplay';
import OrgBreadcrumb from '../components/common/OrgBreadcrumb';

// ─── Nav Types ──────────────────────────────────────────
interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
}

interface NavGroup {
  group: string;
  items: NavItem[];
}

// ─── Main Component ─────────────────────────────────────
const MyProfilePage: React.FC = () => {
  const { user } = useAuth();
  const subscriptions = useSubscriptionStore((s) => s.subscriptions);

  const {
    selectedEmployee,
    isLoading,
    error,
    loadProfile,
    updateEmployee,
  } = useEmployeeProfileStore();

  const [currentView, setCurrentView] = useState('about');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [isEditing, setIsEditing] = useState(false);
  const [sectionHasErrors, setSectionHasErrors] = useState(false);
  const [hierarchyPreview, setHierarchyPreview] = useState<any[] | null>(null);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Build navigation items (self-mode config: profileData, performance, records, settings)
  const navItems: NavGroup[] = useMemo(() => {
    const items: NavGroup[] = [
      {
        group: 'Profile Data',
        items: [
          { id: 'about', label: 'About', icon: UserCircle },
          { id: 'jobdetails', label: 'Job Details', icon: FileText },
        ],
      },
    ];

    if (subscriptions.find(m => m.module_id === 'perform-spark')?.is_active) {
      items.push({
        group: 'Performance',
        items: [
          { id: 'participation', label: 'Participation Summary', icon: ListChecks },
          { id: 'goals', label: 'Goals', icon: Target },
          { id: 'review_cycles', label: 'Review Cycles History', icon: ClockCounterClockwise },
          { id: 'checkins', label: 'Check-ins (1:1s)', icon: Users },
          { id: 'competencies', label: 'Top Competencies', icon: Star },
          { id: 'improvement', label: 'Areas for Improvement', icon: TrendUp },
          { id: 'feedback', label: '360 Feedback', icon: UsersFour },
        ],
      });
    }

    if (subscriptions.find(m => m.module_id === 'comp-builder')?.is_active) {
      items.push({
        group: 'Benefits & Compensation',
        items: [
          { id: 'benefits', label: 'Benefits & Compensation', icon: CurrencyCircleDollar },
        ],
      });
    }

    items.push(
      {
        group: 'Records',
        items: [
          { id: 'documents', label: 'Docustor', icon: FileText },
          { id: 'updates', label: 'Updates', icon: ChatCircle },
        ],
      },
      {
        group: 'Settings',
        items: [
          { id: 'admin', label: 'Admin', icon: GearSix },
        ],
      },
    );

    return items;
  }, [subscriptions]);

  // Editable views (Job Details only — About sections manage their own edit buttons)
  const editableViews = useMemo(() => ['jobdetails'], []);

  // Scroll to top and exit edit mode when view changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
    setIsEditing(false);
    setEditingSection(null);
    setSectionHasErrors(false);
  }, [currentView]);

  // Preserve collapse state for existing groups and initialize new groups as expanded
  useEffect(() => {
    setCollapsedGroups((previous) =>
      navItems.reduce<Record<string, boolean>>((acc, group) => {
        acc[group.group] = previous[group.group] ?? false;
        return acc;
      }, {})
    );
  }, [navItems]);

  const toggleGroup = (groupName: string) => {
    setCollapsedGroups((previous) => ({
      ...previous,
      [groupName]: !previous[groupName],
    }));
  };

  // Load employee profile data
  useEffect(() => {
    if (!user?.user_uuid) return;
    loadProfile(user.user_uuid);
  }, [user?.user_uuid, loadProfile]);

  const handleUpdate = async (updates: Partial<EmployeeDetails>) => {
    await updateEmployee(updates);
  };

  // ── Render section content ──
  const renderSectionContent = () => {
    if (!selectedEmployee) return null;

    switch (currentView) {
      case 'about':
        return (
          <div className="flex flex-col gap-6">
            <AboutSection employee={selectedEmployee} onUpdate={handleUpdate} readOnly={false} disableEdit={editingSection !== null && editingSection !== 'about'} onEditingChange={(editing) => setEditingSection(editing ? 'about' : null)} />
            <div className="w-full h-px bg-slate-200" />
            <EducationProfileSection employee={selectedEmployee} onUpdate={handleUpdate} readOnly={false} disableEdit={editingSection !== null && editingSection !== 'education'} onEditingChange={(editing) => setEditingSection(editing ? 'education' : null)} />
            <div className="w-full h-px bg-slate-200" />
            <ExperienceProfileSection employee={selectedEmployee} onUpdate={handleUpdate} readOnly={false} disableEdit={editingSection !== null && editingSection !== 'experience'} onEditingChange={(editing) => setEditingSection(editing ? 'experience' : null)} />
          </div>
        );

      case 'jobdetails':
        return (
          <div className="flex flex-col gap-6">
            <JobDetailsSection
              employee={selectedEmployee}
              onUpdate={handleUpdate}
              readOnly={false}
              isEditing={isEditing}
              onHierarchyPreview={setHierarchyPreview}
              onValidationChange={setSectionHasErrors}
              hideTitle
            />
            <div className="w-full h-px bg-slate-200" />
            <div className="w-full">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Organization Hierarchy</h3>
              <OrgHierarchyPathDisplay path={hierarchyPreview || selectedEmployee.org_path} />
            </div>
          </div>
        );

      // Performance sections
      case 'participation':
        return <AnalyticsSection employee={selectedEmployee} sectionId="participation" hideTitle />;
      case 'goals':
        return <AnalyticsSection employee={selectedEmployee} sectionId="goals" hideTitle />;
      case 'review_cycles':
        return <AnalyticsSection employee={selectedEmployee} sectionId="review_cycles" hideTitle />;
      case 'checkins':
        return <AnalyticsSection employee={selectedEmployee} sectionId="checkins" hideTitle />;
      case 'competencies':
        return <AnalyticsSection employee={selectedEmployee} sectionId="competencies" hideTitle />;
      case 'improvement':
        return <AnalyticsSection employee={selectedEmployee} sectionId="improvement" hideTitle />;
      case 'feedback':
        return <AnalyticsSection employee={selectedEmployee} sectionId="feedback" hideTitle />;

      case 'benefits':
        return <BenefitsSection employee={selectedEmployee} />;

      case 'documents':
        return <DocumentsSection employee={selectedEmployee} />;

      case 'updates':
        return <UpdatesSection employee={selectedEmployee} />;

      case 'admin':
        return <AdminSection employee={selectedEmployee} />;

      default:
        return (
          <div className="flex flex-col gap-6">
            <AboutSection employee={selectedEmployee} onUpdate={handleUpdate} readOnly={false} disableEdit={editingSection !== null && editingSection !== 'about'} onEditingChange={(editing) => setEditingSection(editing ? 'about' : null)} />
            <div className="w-full h-px bg-slate-200" />
            <EducationProfileSection employee={selectedEmployee} onUpdate={handleUpdate} readOnly={false} disableEdit={editingSection !== null && editingSection !== 'education'} onEditingChange={(editing) => setEditingSection(editing ? 'education' : null)} />
            <div className="w-full h-px bg-slate-200" />
            <ExperienceProfileSection employee={selectedEmployee} onUpdate={handleUpdate} readOnly={false} disableEdit={editingSection !== null && editingSection !== 'experience'} onEditingChange={(editing) => setEditingSection(editing ? 'experience' : null)} />
          </div>
        );
    }
  };

  // ── Loading / error states ──
  if (isLoading && !selectedEmployee) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" className="mx-auto mb-4" />
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (error && !selectedEmployee) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!selectedEmployee) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-gray-600">Profile not found</p>
      </div>
    );
  }

  return (
    <div className="h-full bg-white flex flex-col overflow-hidden">
      <div className="flex-1 overflow-hidden max-w-[1600px] mx-auto w-full px-6 py-6 flex flex-col">
        {/* Profile Card - Full Width Top */}
        <div className="bg-white rounded-xl border border-slate-200 px-5 py-3 flex items-center gap-4 shadow-sm mb-6 shrink-0">
          {/* Avatar */}
          <div className="relative shrink-0">
            <div className="ring-2 ring-slate-50 rounded-full inline-block">
              <ProfilePhotoEditor
                employee={selectedEmployee}
                onUpdate={handleUpdate}
                size="small"
              />
            </div>
          </div>

          {/* Name & Title */}
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-900 leading-tight">{selectedEmployee.name}</h2>
            <p className="text-sm text-slate-500 leading-tight">
              {selectedEmployee.jobTitle || 'No Job Title'}
            </p>
          </div>

          {/* Divider */}
          <div className="h-8 w-px bg-slate-200 shrink-0" />

          {/* Org Path Breadcrumb */}
          <div className="min-w-0 shrink">
            {selectedEmployee.org_path && selectedEmployee.org_path.length > 0 ? (
              <OrgBreadcrumb
                path={selectedEmployee.org_path}
                mode="full"
                interactive={false}
                className="flex-wrap"
              />
            ) : selectedEmployee.unit ? (
              <div className="flex items-center gap-1.5 text-sm text-slate-600">
                <Buildings className="w-4 h-4 text-slate-400" />
                <span>{selectedEmployee.unit}</span>
              </div>
            ) : null}
          </div>

          {/* Divider */}
          <div className="h-8 w-px bg-slate-200 shrink-0" />

          {/* Status + ID badges */}
          <div className="flex items-center gap-2 shrink-0">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-slate-100 border border-slate-200">
              <div className={`w-1.5 h-1.5 rounded-full ${
                selectedEmployee.status === 'Inactive' ? 'bg-yellow-500'
                  : selectedEmployee.status === 'Terminated' ? 'bg-red-500'
                  : 'bg-emerald-500'
              }`} />
              <span className="text-xs font-medium text-slate-600">
                {selectedEmployee.status || 'Active'}
              </span>
            </div>
            {selectedEmployee.employee_id && (
              <div className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-slate-100 border border-slate-200">
                <span className="text-xs font-medium text-slate-600">
                  ID: {selectedEmployee.employee_id}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-6 flex-1 overflow-hidden">
          {/* Left Sidebar */}
          <div className="w-80 shrink-0 flex flex-col gap-4 h-full overflow-hidden">
            {/* Navigation Menu */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex-1 min-h-0 overflow-y-auto scrollbar-sidebar">
              <nav className="flex flex-col gap-6">
                {navItems.map((group) => (
                  <div key={group.group}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.group)}
                      className="w-full flex items-center justify-between mb-2 px-3"
                      aria-expanded={!collapsedGroups[group.group]}
                    >
                      <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider text-left">
                        {group.group}
                      </h3>
                      <CaretDown
                        className={`w-3.5 h-3.5 text-slate-400 transition-transform ${
                          collapsedGroups[group.group] ? '-rotate-90' : 'rotate-0'
                        }`}
                      />
                    </button>
                    {!collapsedGroups[group.group] && (
                      <div className="flex flex-col gap-1">
                        {group.items.map((item) => (
                          <button
                            key={item.id}
                            onClick={() => setCurrentView(item.id)}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              currentView === item.id
                                ? 'bg-[#4B45E5]/10 text-[#4B45E5]'
                                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                            }`}
                          >
                            <item.icon
                              className={`w-4 h-4 ${currentView === item.id ? 'text-[#4B45E5]' : 'text-slate-400'}`}
                            />
                            {item.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0 h-full flex flex-col overflow-hidden">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col h-full overflow-hidden relative">
              {/* Loading overlay — covers entire card including scrollable content */}
              {isLoading && selectedEmployee && (
                <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center rounded-xl">
                  <Spinner size="lg" />
                </div>
              )}
              {/* Section Header */}
              <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center shrink-0">
                <h1 className="text-xl font-bold text-slate-900 capitalize">
                  {navItems.flatMap((g) => g.items).find((i) => i.id === currentView)?.label || 'About'}
                </h1>
                {/* Edit/Done button for editable views */}
                {editableViews.includes(currentView) && (
                  <div className="flex items-center gap-2">
                    {isEditing ? (
                      <button
                        onClick={() => setIsEditing(false)}
                        disabled={sectionHasErrors}
                        className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${sectionHasErrors ? 'bg-gray-400 text-gray-200 cursor-not-allowed opacity-50' : 'text-white bg-indigo-600 hover:bg-indigo-700'}`}
                      >
                        <Save className="w-4 h-4" />
                        Save
                      </button>
                    ) : (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-md transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Scrollable Content */}
              <div ref={contentRef} className="p-6 overflow-y-auto flex-1 min-h-0">
                {renderSectionContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyProfilePage;
